import type * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { COLLECTION_TITLE_MAX_LENGTH } from '@gitbook/api-client';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

import { useCreateInOwner } from '../ContentListing';

type CreateCollectionFormInputs = {
    title: string;
};

type CreateCollectionDialogProps = CreateCollectionFormProps;

/**
 * Modal to create a collection.
 */
export function CreateCollectionDialog(props: CreateCollectionDialogProps): React.ReactElement {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Create collection</Heading>
                <Description>
                    Collections are folders for your spaces. You can use them to organize your
                    content.
                </Description>
            </DialogHeader>
            <CreateCollectionForm {...props} />
        </Dialog>
    );
}

interface CreateCollectionFormProps {
    organizationId: string;
    collectionId?: string;
}

function CreateCollectionForm(props: CreateCollectionFormProps) {
    const [isPending, startDialogAction] = useDialogAction();
    const { createCollection } = useCreateInOwner({
        organization: props.organizationId,
        collection: props.collectionId,
    });

    assert(createCollection, 'Invalid context: cannot create a collection here');

    const form = useForm<CreateCollectionFormInputs>({
        values: { title: '' },
    });

    const onSubmit: SubmitHandler<CreateCollectionFormInputs> = async ({ title }) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await createCollection({ title });
                toast.success('New collection added to your spaces', {
                    description:
                        'Collections are folders for your spaces. Your new collection has been added at the end of your spaces list in the sidebar.',
                });
            }, 'An error occurred while creating new collection');
            close();
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <TextField
                    name="title"
                    required
                    minLength={1}
                    maxLength={COLLECTION_TITLE_MAX_LENGTH}
                >
                    <Label>Title</Label>
                    <TextInput autoFocus placeholder="My spaces" disabled={isPending} />
                    <FieldError />
                </TextField>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Create" />
            </DialogFooter>
        </Form>
    );
}
